import { Button } from '@/ui/atoms/button/Button'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { AlertTriangle, Check, Circle, Loader2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import type { BigBlocksFlowStep, StepStatus } from '../logic/constants'
import { useBigBlocksToggle } from '../logic/useBigBlocksToggle'

interface FlowStep {
  id: BigBlocksFlowStep
  label: string
  status: StepStatus
  error?: string
}

interface BigBlocksSwapFlowProps {
  /** Whether to switch back to small blocks after swap */
  switchBackAfter: boolean
  /** Execute the swap - should return true if successful */
  executeSwap: () => Promise<boolean>
  /** Current swap state message */
  swapState: string
  /** Whether swap encountered an error */
  swapError: boolean
  /** Called when the flow completes successfully */
  onComplete: () => void
  /** Called when the user cancels */
  onCancel: () => void
}

const STEP_LABELS: Record<BigBlocksFlowStep, string> = {
  'approve-agent': 'Approve temporary agent',
  'enable-big-blocks': 'Enable big blocks',
  'revoke-agent': 'Revoke agent',
  'execute-swap': 'Execute swap',
  'switch-back': 'Switch back to small blocks',
}

function StepIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case 'complete':
      return <Check className="h-4 w-4 text-green-500" />
    case 'in-progress':
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />
    case 'error':
      return <X className="h-4 w-4 text-red-500" />
    case 'pending':
    default:
      return <Circle className="h-4 w-4 text-muted-foreground" />
  }
}

export function BigBlocksSwapFlow({
  switchBackAfter,
  executeSwap,
  swapState,
  swapError,
  onComplete,
  onCancel,
}: BigBlocksSwapFlowProps) {
  const { setIsUsingBigBlocks, currentStep: bigBlocksStep } = useBigBlocksToggle()

  const [flowState, setFlowState] = useState<'running' | 'swap-failed' | 'complete'>('running')
  const [currentPhase, setCurrentPhase] = useState<'enable' | 'swap' | 'switch-back'>('enable')
  const [steps, setSteps] = useState<FlowStep[]>(() => {
    const baseSteps: FlowStep[] = [
      { id: 'approve-agent', label: STEP_LABELS['approve-agent'], status: 'pending' },
      { id: 'enable-big-blocks', label: STEP_LABELS['enable-big-blocks'], status: 'pending' },
      { id: 'revoke-agent', label: STEP_LABELS['revoke-agent'], status: 'pending' },
      { id: 'execute-swap', label: STEP_LABELS['execute-swap'], status: 'pending' },
    ]
    if (switchBackAfter) {
      baseSteps.push({ id: 'switch-back', label: STEP_LABELS['switch-back'], status: 'pending' })
    }
    return baseSteps
  })
  const [swapFailError, setSwapFailError] = useState<string | null>(null)
  const [isSwitchingBack, setIsSwitchingBack] = useState(false)

  const updateStepStatus = useCallback((stepId: BigBlocksFlowStep, status: StepStatus, error?: string) => {
    setSteps((prev) => prev.map((step) => (step.id === stepId ? { ...step, status, error } : step)))
  }, [])

  // Map bigBlocksStep to our flow steps (only during 'enable' phase)
  useEffect(() => {
    // Only map steps during the initial "enable big blocks" phase
    // During switch-back phase, we handle the step updates directly in runFlow
    if (currentPhase !== 'enable') return

    if (bigBlocksStep === 'approve-agent') {
      updateStepStatus('approve-agent', 'in-progress')
    } else if (bigBlocksStep === 'modify-settings') {
      updateStepStatus('approve-agent', 'complete')
      updateStepStatus('enable-big-blocks', 'in-progress')
    } else if (bigBlocksStep === 'revoke-agent') {
      updateStepStatus('enable-big-blocks', 'complete')
      updateStepStatus('revoke-agent', 'in-progress')
    }
  }, [bigBlocksStep, updateStepStatus, currentPhase])

  // Run the flow on mount
  useEffect(() => {
    let cancelled = false

    async function runFlow() {
      try {
        // Steps 1-3: Enable big blocks (approve agent, modify, revoke)
        let bigBlocksSuccess: boolean
        try {
          bigBlocksSuccess = await setIsUsingBigBlocks(true)
        } catch (err) {
          // Error thrown by the hook - mark the current step as failed
          if (cancelled) return
          const errorMessage = err instanceof Error ? err.message : 'Failed to enable big blocks'
          const failedStep = ['approve-agent', 'enable-big-blocks', 'revoke-agent'].find((stepId) => {
            const step = steps.find((s) => s.id === stepId)
            return step?.status === 'in-progress'
          }) as BigBlocksFlowStep | undefined
          if (failedStep) {
            updateStepStatus(failedStep, 'error', errorMessage)
          }
          return
        }

        if (cancelled) return

        if (!bigBlocksSuccess) {
          // User rejected - just exit silently
          return
        }

        updateStepStatus('revoke-agent', 'complete')

        // Step 4: Execute swap
        setCurrentPhase('swap')
        updateStepStatus('execute-swap', 'in-progress')

        const swapSuccess = await executeSwap()

        if (cancelled) return

        if (!swapSuccess) {
          updateStepStatus('execute-swap', 'error', 'Swap failed')
          setSwapFailError('The swap transaction failed. Big blocks are still enabled.')
          setFlowState('swap-failed')
          return
        }

        updateStepStatus('execute-swap', 'complete')

        // Step 5: Switch back (if enabled)
        if (switchBackAfter) {
          setCurrentPhase('switch-back')
          updateStepStatus('switch-back', 'in-progress')

          const switchBackSuccess = await setIsUsingBigBlocks(false)

          if (cancelled) return

          if (switchBackSuccess) {
            updateStepStatus('switch-back', 'complete')
          } else {
            // Switch back failed, but swap succeeded - not critical
            updateStepStatus('switch-back', 'error', 'Failed to switch back')
          }
        }

        setFlowState('complete')
      } catch (err) {
        console.error('Big blocks swap flow error:', err)
      }
    }

    runFlow()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  // Handle switching back after swap failure
  const handleSwitchBack = async () => {
    setIsSwitchingBack(true)
    try {
      await setIsUsingBigBlocks(false)
      onCancel()
    } finally {
      setIsSwitchingBack(false)
    }
  }

  const handleStayOnBigBlocks = () => {
    onCancel()
  }

  // Render swap failed state
  if (flowState === 'swap-failed') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-red-400">
          <AlertTriangle className="h-5 w-5" />
          <Typography variant="h4">Swap Failed</Typography>
        </div>

        <div className="space-y-2">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center gap-3">
              <span className="w-4 text-muted-foreground text-xs">{index + 1}.</span>
              <StepIcon status={step.status} />
              <span
                className={cn(
                  'text-sm',
                  step.status === 'complete' && 'text-muted-foreground',
                  step.status === 'error' && 'text-red-400',
                )}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-border bg-background/50 p-4">
          <Typography variant="p" className="text-secondary-foreground text-sm">
            {swapFailError}
          </Typography>
          <Typography variant="p" className="mt-2 text-secondary-foreground text-sm">
            Would you like to switch back to small blocks?
          </Typography>
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleStayOnBigBlocks} disabled={isSwitchingBack} className="flex-1">
            Stay on Big Blocks
          </Button>
          <Button onClick={handleSwitchBack} disabled={isSwitchingBack} className="flex-1">
            {isSwitchingBack ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Switching...
              </>
            ) : (
              'Switch Back'
            )}
          </Button>
        </div>
      </div>
    )
  }

  // Render complete state
  if (flowState === 'complete') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-green-500">
          <Check className="h-5 w-5" />
          <Typography variant="h4">Swap Complete!</Typography>
        </div>

        <div className="space-y-2">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center gap-3">
              <span className="w-4 text-muted-foreground text-xs">{index + 1}.</span>
              <StepIcon status={step.status} />
              <span className={cn('text-sm', step.status === 'complete' && 'text-muted-foreground')}>{step.label}</span>
            </div>
          ))}
        </div>

        <Button onClick={onComplete} className="w-full">
          Done
        </Button>
      </div>
    )
  }

  // Render running state
  return (
    <div className="space-y-4">
      <Typography variant="h4">Enabling Big Blocks & Swapping</Typography>

      <div className="space-y-2">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center gap-3">
            <span className="w-4 text-muted-foreground text-xs">{index + 1}.</span>
            <StepIcon status={step.status} />
            <span
              className={cn(
                'text-sm',
                step.status === 'complete' && 'text-muted-foreground',
                step.status === 'in-progress' && 'text-foreground',
                step.status === 'pending' && 'text-muted-foreground',
                step.status === 'error' && 'text-red-400',
              )}
            >
              {step.label}
              {step.status === 'in-progress' && step.id === 'execute-swap' && swapState && (
                <span className="ml-2 text-muted-foreground text-xs">({swapState})</span>
              )}
            </span>
          </div>
        ))}
      </div>

      {swapError && (
        <Typography variant="p" className="text-red-400 text-sm">
          Swap error occurred. Please try again.
        </Typography>
      )}

      <Button variant="secondary" onClick={onCancel} className="w-full">
        Cancel
      </Button>
    </div>
  )
}
